import {
  EmptyStateBase,
  Page,
  useModals,
  parseUrlHelper,
  Box,
  Button,
  Stack,
  Title,
} from "@skailer-com/uikit";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { ApplicantProfile } from "@/pages/recruit/candidates/components/ApplicantProfile.tsx";
import { CandidateCard } from "@/pages/recruit/candidates/components/CandidateCard.tsx";
import { ApplicantAssignToJobModal } from "@/pages/recruit/candidates/widgets/ApplicantAssignToJobModal.tsx";
import {
  useApplicantDetails,
  useApplicantShortCache,
} from "@/queries/recruit/use-applicants.query.ts";
import { useCandidatesByApplicant } from "@/queries/recruit/use-candidates.query.ts";
import { useJobDetails } from "@/queries/recruit/use-jobs.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useEmployeeAppConfigStore } from "@/stores/employee.store.ts";
import { ApiCandidate } from "@/types/recruit/applicant.types.ts";
import { JobStatus } from "@/types/recruit/job.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { Allotment } from "allotment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { useDebounce } from "react-use";

export const ApplicantDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { recruitRightPaneSize, setRecruitRightPaneSize } = useEmployeeAppConfigStore();
  const modalsService = useModals();
  const { canIEdit } = usePermissions();

  const currentApplicantId = useMemo(() => (id ? parseInt(id, 10) : undefined), [id]);

  const { data: applicantFull } = useApplicantDetails(currentApplicantId);
  const { data: applicantShort } = useApplicantShortCache(currentApplicantId);
  const { data: candidates } = useCandidatesByApplicant(currentApplicantId);

  const [searchParams] = useSearchParams();
  const jobId = parseUrlHelper.getNumberArray(searchParams, "job_id");
  const openCandidate = Number(searchParams.get("candidate_id")) || undefined;

  const { data: jobDetails } = useJobDetails(jobId && jobId.length == 1 ? jobId[0] : undefined);

  const title = useMemo(() => {
    if (applicantFull) {
      return `${applicantFull.first_name} ${applicantFull.last_name}`;
    }
    if (jobDetails) {
      return jobDetails.title;
    }
    return undefined;
  }, [jobDetails, applicantFull]);

  useTitle(title, t("ats:recruit"));

  const candidatesSorted = useMemo(() => {
    if (!candidates) return [];

    const filteredCandidates = candidates.filter((candidate) => {
      return (
        candidate.job.status != JobStatus.archived ||
        (jobId && jobId.length == 1 && candidate.job.id == jobId[0])
      );
    });

    return filteredCandidates.sort((a, b) => {
      if (a.id === openCandidate) return -1;
      if (b.id === openCandidate) return 1;

      if (jobId && jobId.length === 1) {
        if (a.job.id === jobId[0]) return -1;
        if (b.job.id === jobId[0]) return 1;
      }

      return 0;
    });
  }, [candidates, jobId]);

  const candidatesOnArchivedJobs = useMemo(() => {
    if (!candidates) return [];

    return candidates.filter((candidate) => {
      return (
        candidate.job.status == JobStatus.archived &&
        !(jobId && jobId.length == 1 && candidate.job.id == jobId[0])
      );
    });
  }, [candidates, jobId]);

  const handleAssignToJob = useCallback(() => {
    if (!applicantShort) return;

    modalsService.openModal(ApplicantAssignToJobModal, {
      applicant: applicantShort,
    });
  }, [applicantShort]);

  const [lastPanelSize, setLastPanelSize] = useState<number | undefined>(undefined);
  useDebounce(
    () => {
      if (lastPanelSize != undefined) {
        analyticsService.trackEvent(analyticEvents.pageAction, {
          [analyticProperties.actionType]: "Employee App Config Changed",
          [analyticProperties.title]: "Recruiting Right Pane Size",
          [analyticProperties.id]: lastPanelSize,
        });
      }
    },
    500,
    [lastPanelSize]
  );

  // Фиксим скролл вверх при смене кандидата (https://app.clickup.com/t/8697w0771)
  const profileContainer = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    profileContainer.current?.scrollTo({
      top: 0,
    });
  }, [currentApplicantId]);

  return (
    <Allotment
      onChange={(sizes: number[]) => {
        if (sizes.length > 1) {
          setRecruitRightPaneSize(sizes[1] | 0);
          setLastPanelSize(sizes[1] | 0);
        }
      }}
    >
      <Allotment.Pane>
        <Stack
          ref={profileContainer}
          direction={"vertical"}
          className={"h-full overflow-y-auto grow bg-light shadow-left"}
        >
          <ApplicantProfile
            applicant={applicantFull ?? applicantShort}
            applicantFull={applicantFull}
          />
        </Stack>
      </Allotment.Pane>
      <Allotment.Pane minSize={200} preferredSize={recruitRightPaneSize}>
        <Stack
          direction={"vertical"}
          className={"h-full overflow-y-auto bg-light shadow-left shrink-0"}
        >
          <Box className={"bg-light"}>
            <Page.Header title={t("ats:jobs")} nowrap={true}>
              {canIEdit(ApiPermission.manage_candidates) && (
                <Stack className={"overflow-hidden"}>
                  <Button onClick={handleAssignToJob} variant={"secondary"}>
                    {t("ats:assign_to_job")}
                  </Button>
                </Stack>
              )}
            </Page.Header>
          </Box>
          <Stack>
            {candidatesSorted &&
              currentApplicantId &&
              candidatesSorted.map((candidate: ApiCandidate, index: number) => (
                <CandidateCard
                  key={`candidate-${candidate.id}`}
                  isFirst={index === 0}
                  collapsed={index > 0 && openCandidate !== candidate.id}
                  candidate={candidate}
                  applicantId={currentApplicantId}
                />
              ))}
            {currentApplicantId && candidatesOnArchivedJobs.length > 0 && (
              <>
                <Box className={" px-6 border-dark/5 border-b"}>
                  <Title
                    header={t("ats:job_status_archived")}
                    size={"xs"}
                    paddingTop={true}
                    paddingBottom={true}
                  />
                </Box>
                {candidatesOnArchivedJobs.map((candidate: ApiCandidate) => (
                  <CandidateCard
                    key={`candidate-${candidate.id}`}
                    isFirst={false}
                    collapsed={openCandidate !== candidate.id && candidates?.length !== 1}
                    candidate={candidate}
                    applicantId={currentApplicantId}
                  />
                ))}
              </>
            )}
          </Stack>
          {candidates && candidates.length == 0 && (
            <EmptyStateBase
              emoji={":sloth:"} //🦥
              title={t("ats:no_jobs_assigned")}
              primaryButton={
                <Button onClick={handleAssignToJob} size={"sm"} variant={"secondary"}>
                  {t("ats:assign_to_job")}
                </Button>
              }
            />
          )}
        </Stack>
      </Allotment.Pane>
    </Allotment>
  );
};
